import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import { ReactElement, ReactNode } from 'react'
import { SearchIcon } from 'src/v3/core/icons'

type LinkedService = {
  id: string
  name: string
}

type CourseRef = {
  courseID: string
  courseName: string
}

export type LibraryFilterState = {
  linkedServiceFilter: string
  courseFilter: string
  search: string
  onLinkedServiceFilterChange: (v: string) => void
  onCourseFilterChange: (v: string) => void
  onSearchChange: (v: string) => void
  onResetFilters: () => void
  isFiltered: boolean
  distinctCourses: CourseRef[]
  filteredItemCount: number
  totalItemCount: number
}

type DeleteDialogConfig = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  itemName: string | undefined
  isDeleting: boolean
}

type LibraryPageLayoutProps = {
  pageTitle: string
  createButton: ReactNode
  itemCountLabel: string
  linkedServices: LinkedService[]
  filters: LibraryFilterState
  isLoading: boolean
  emptyMessage: string
  noResultsMessage: string
  loadingAriaLabel?: string
  deleteDialog: DeleteDialogConfig
  children: ReactNode
  itemsLayout?: 'grid' | 'list'
}

export default function LibraryPageLayout({
  pageTitle,
  createButton,
  itemCountLabel,
  linkedServices,
  filters,
  isLoading,
  emptyMessage,
  noResultsMessage,
  loadingAriaLabel,
  deleteDialog,
  children,
  itemsLayout = 'grid',
}: LibraryPageLayoutProps): ReactElement {
  return (
    <Container maxWidth="xl" className="py-6">
      <Box className="flex items-center gap-3 mb-6">
        <Typography variant="h4" component="h1" data-tour="library-page-header">
          {pageTitle}
        </Typography>
      </Box>
      <Box className="flex flex-wrap items-center justify-between gap-2 mb-6">
        <Box data-tour="library-create-button">{createButton}</Box>
        <Box className="flex items-center justify-end gap-2">
          <Chip label={itemCountLabel} size="medium" color="primary" />
          {filters.isFiltered && (
            <Button
              size="medium"
              variant="outlined"
              color="secondary"
              onClick={filters.onResetFilters}
              className="whitespace-nowrap"
            >
              Reset Filters
            </Button>
          )}
        </Box>
      </Box>
      <Box
        className="grid grid-cols-1 gap-6 mb-6 lg:grid-cols-3"
        data-tour="library-filters"
      >
        <FormControl sx={{ backgroundColor: 'background.paperDark' }}>
          <InputLabel id="linked-service-filter">Linked Service</InputLabel>
          <Select
            labelId="linked-service-filter"
            label="Linked Service"
            value={filters.linkedServiceFilter}
            size="medium"
            onChange={(e) =>
              filters.onLinkedServiceFilterChange(e.target.value)
            }
          >
            <MenuItem value="all">
              <em>All Linked Services</em>
            </MenuItem>
            {linkedServices.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ backgroundColor: 'background.paperDark' }}>
          <InputLabel id="course-filter">Course Association</InputLabel>
          <Select
            labelId="course-filter"
            label="Course Association"
            value={filters.courseFilter}
            size="medium"
            onChange={(e) => filters.onCourseFilterChange(e.target.value)}
          >
            <MenuItem value="all">
              <em>All Courses</em>
            </MenuItem>
            <MenuItem value="none">No Course Association</MenuItem>
            {filters.distinctCourses.map((c) => (
              <MenuItem key={c.courseID} value={c.courseID}>
                {c.courseName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Search"
          placeholder="Search by name..."
          value={filters.search}
          onChange={(e) => filters.onSearchChange(e.target.value)}
          size="medium"
          sx={{ backgroundColor: 'background.paperDark' }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>
      {isLoading ? (
        <Box
          className="flex justify-center py-12"
          role="status"
          aria-label={loadingAriaLabel ?? `Loading ${pageTitle.toLowerCase()}`}
        >
          <CircularProgress aria-hidden="true" />
        </Box>
      ) : filters.filteredItemCount === 0 ? (
        <Typography color="text.secondary" className="py-12 text-center">
          {filters.totalItemCount === 0 ? emptyMessage : noResultsMessage}
        </Typography>
      ) : itemsLayout === 'list' ? (
        <Box className="flex flex-col gap-2">{children}</Box>
      ) : (
        <Box className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {children}
        </Box>
      )}
      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleteDialog.isDeleting && deleteDialog.onClose()}
        fullWidth
        aria-labelledby="delete-library-item-dialog-title"
        maxWidth="xs"
      >
        <DialogTitle id="delete-library-item-dialog-title">
          {deleteDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "
            {deleteDialog.itemName ?? 'this item'}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={deleteDialog.onClose}
            disabled={deleteDialog.isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={deleteDialog.onConfirm}
            disabled={deleteDialog.isDeleting}
            startIcon={
              deleteDialog.isDeleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
          >
            {deleteDialog.isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
